import { supabase } from "@/integrations/supabase/client";
import { isLocalAudio, type SentenceAudioBlock } from "./audioBlockMap";

export interface PlaybackManifestClip { cached: boolean; url: string | null; voice_revision: string; bytes: number }
export async function playbackManifest(screenplayId: string, clips: SentenceAudioBlock[], revisions: ReadonlyMap<string, string>) {
  const spoken = clips.filter(c => !isLocalAudio(c));
  if (spoken.length !== clips.length) {
    const manifest = await playbackManifest(screenplayId, spoken, revisions);
    let index = 0;
    return clips.map(c => isLocalAudio(c) ? { cached: true, url: null, voice_revision: "local", bytes: 0 } : manifest[index++]);
  }
  if (!clips.length) return [] as PlaybackManifestClip[];
  const { data, error } = await supabase.auth.getSession();
  if (error || !data.session?.access_token) throw new Error("Sign in to play this screenplay.");
  const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/audio-preparation`, {
    method: "POST", headers: { Authorization: `Bearer ${data.session.access_token}`,
      apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY, "Content-Type": "application/json" },
    body: JSON.stringify({ action: "playback-plan", screenplay_id: screenplayId,
      items: clips.map(c => ({ voice_id: c.voiceId, voice_revision: revisions.get(c.voiceId),
        text: c.text, synthesis_options: c.synthesisOptions,
        ...(c.cacheSources && c.cacheSources.length>1 ? {cache_sources:c.cacheSources.map(s=>({text:s.text,synthesis_options:s.synthesisOptions}))} : {}) })) }), signal: AbortSignal.timeout(25000),
  });
  const result = await response.json();
  if (!response.ok) throw Object.assign(new Error(result.error || "Unable to check playback audio."), { status: response.status });
  if (!Array.isArray(result.clips) || result.clips.length !== clips.length || result.clips.some((c: PlaybackManifestClip, i: number) =>
    typeof c?.cached !== "boolean" || c.voice_revision !== revisions.get(clips[i].voiceId))) throw new Error("Invalid playback audio manifest.");
  return result.clips as PlaybackManifestClip[];
}
